import React from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { FiBookOpen, FiClock, FiGrid } from "react-icons/fi";
import { participantApi } from "../../../api/participant";
import Loader from "../../../components/loader/Loader";
import CardImage from "../../../assets/online-course.png";

const collection = (response) => response?.data || response?.resourceData || response || [];

const getTrackerId = (item) =>
  item?.trackerId || item?.cohortParticipantId || item?.trackerid || item?.id;

const getLearningPathId = (item) => item?.learningPathId || item?.journeyId || item?.id;

const SELECTED_PATH_KEY = "participant:selectedLearningPath";

const percentFromItem = (item) => {
  if (typeof item?.completionPercentage === "number") return item.completionPercentage;
  if (typeof item?.progressPercentage === "number") return item.progressPercentage;
  const completed = item?.completedCourses || item?.numberOfCompletedCourses || 0;
  const total = item?.totalCourses || item?.numberOfCourses || item?.coursesCount || 1;
  return Math.min(100, Math.round((completed / (total || 1)) * 100));
};

const LearningPaths = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery(
    ["participant-learning-paths"],
    participantApi.getLearningPaths,
    { staleTime: 60000 }
  );

  const learningPaths = Array.isArray(collection(data)) ? collection(data) : [];

  const openLearningPath = (item) => {
    const trackerId = getTrackerId(item);
    const learningPathId = getLearningPathId(item);
    if (!trackerId || !learningPathId) return;

    sessionStorage.setItem(
      SELECTED_PATH_KEY,
      JSON.stringify({
        trackerId,
        learningPathId,
        learningPathName: item?.learningPathName || item?.name || "Learning Path",
      })
    );

    navigate(`/participant/courses-and-learning-path/${trackerId}/${learningPathId}`);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <FiBookOpen className="text-[#0A3059]" />
        <h1 className="text-xl font-semibold text-[#0A3059]">Learning Path & Courses</h1>
      </div>

      {isLoading ? (
        <div className="bg-white p-6 rounded-xl border border-gray-100">
          <Loader />
        </div>
      ) : learningPaths.length ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 max-w-[1400px]">
          {learningPaths.map((path) => {
            const progress = percentFromItem(path);
            const title = path?.learningPathName || path?.name || "Learning Path";
            const coursesCount = path?.totalCourses || path?.numberOfCourses || path?.coursesCount || 0;
            const completed = path?.completedCourses || path?.numberOfCompletedCourses || 0;
            const trackerStatus = path?.status || "not_started";
            const isLocked = path?.isLocked === true;

            return (
              <div key={`${getTrackerId(path)}-${getLearningPathId(path)}`} className="w-full bg-white rounded-md p-3 shadow border border-gray-200">
                <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                  <div className="w-full">
                    <p className="text-base leading-5 font-semibold text-gray-800">{title}</p>

                    {path?.endDate ? (
                      <p className="text-[#ED6F1E] text-[11px] mt-3">Ended on {new Date(path.endDate).toLocaleDateString()}</p>
                    ) : null}

                    <div className="w-full mt-2 h-1 rounded-full bg-gray-200 overflow-hidden max-w-[330px]">
                      <div className="h-1 bg-[#8CB33E]" style={{ width: `${progress}%` }} />
                    </div>

                    <div className="w-full mt-2 flex items-center justify-between">
                      <p className="text-[11px] font-medium text-gray-700">
                        {completed} of {coursesCount} {coursesCount > 1 ? "courses" : "course"} ({progress}%)
                      </p>
                      <div className="hidden md:flex items-center gap-1 text-gray-500 text-[11px]">
                        <FiGrid className="w-4 h-4" />
                        <span>No of courses {coursesCount}</span>
                      </div>
                    </div>

                    <div className="w-full mt-3 flex items-end justify-between">
                      <div className="flex items-center gap-1 text-gray-500 text-[11px]">
                        <FiClock className="w-4 h-4" />
                        <span>
                          {path?.startDate && path?.endDate
                            ? `${new Date(path.startDate).toLocaleDateString()} - ${new Date(path.endDate).toLocaleDateString()}`
                            : "N/A"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1.5 border border-[#E5ECF5] bg-[#E5ECF5] rounded text-[11px] text-[#0A3059] capitalize">
                          {String(trackerStatus).replaceAll("_", " ")}
                        </span>
                        {isLocked ? (
                          <button type="button" disabled className="bg-[#E99696] text-[11px] text-white min-w-[100px] px-3 py-1.5 rounded-md">
                            Locked
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openLearningPath(path)}
                            className="bg-[#0A3059] text-[11px] border border-[#0A3059] hover:bg-[#082649] text-white min-w-[100px] px-3 py-1.5 rounded-md"
                          >
                            Continue
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="hidden md:flex items-center justify-end w-[170px]">
                    <img src={CardImage} alt="Learning path" className="w-[150px] h-[100px] object-contain" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-xl border border-gray-100 text-sm text-gray-500">
          No learning path assigned yet.
        </div>
      )}
    </div>
  );
};

export default LearningPaths;
